// src/utils/identityEmail.js
//
// Pass 88.4 — a NARROWLY-SCOPED identity-email canonicalizer, used ONLY to decide whether two addresses
// refer to the SAME login mailbox when accepting an organization invite. It is deliberately NOT a general
// email normalizer and NEVER changes a stored email or a delivery `to` address — it is comparison-only.
//
// Rules:
//   - lowercase + trim
//   - strip any stray trailing dot(s) (format hygiene; same mailbox)
//   - for consumer Google mail ONLY (`gmail.com` and `googlemail.com` are the SAME Google mailbox),
//     canonicalize the domain to `gmail.com`
//
// Deliberately OUT OF SCOPE (we do NOT do these — they would risk collapsing distinct identities):
//   - no dot-stripping or `+tag` removal in the local part
//   - no aliasing of any domain other than gmail.com/googlemail.com
//   - no fuzzy matching; a different local part or an unrelated domain is NOT a match.

export function canonicalizeIdentityEmail(email = '') {
  const trimmed = String(email || '').trim().toLowerCase().replace(/\.+$/, '');
  const at = trimmed.lastIndexOf('@');
  // No usable domain (missing '@', leading '@', or trailing '@') → return as-is; nothing to alias.
  if (at <= 0 || at === trimmed.length - 1) return trimmed;
  const local = trimmed.slice(0, at);
  const domain = trimmed.slice(at + 1);
  const canonicalDomain = domain === 'googlemail.com' || domain === 'gmail.com' ? 'gmail.com' : domain;
  return `${local}@${canonicalDomain}`;
}

// True when both addresses resolve to the SAME login mailbox under the narrow rules above.
// Empty/invalid inputs never match.
export function identityEmailsMatch(a = '', b = '') {
  const ca = canonicalizeIdentityEmail(a);
  const cb = canonicalizeIdentityEmail(b);
  return Boolean(ca && cb && ca === cb);
}
